import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { pb } from '@/lib/pocketbase';
import { useToast } from '@/hooks/use-toast';
import { setUserOffline } from '@/utils/onlineStatusUtils';

interface UseInactivityLogoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
  enabled?: boolean;
}

// Singleton state to ensure only one instance runs
const inactivityManager = {
  isInitialized: false,
  lastActiveTime: Date.now(),
  timeoutId: null as NodeJS.Timeout | null,
  warningId: null as NodeJS.Timeout | null,
  warningShown: false,
  eventListeners: [] as Array<{ event: string; handler: EventListener }>,
  
  reset() {
    this.lastActiveTime = Date.now();
    this.warningShown = false;
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.warningId) clearTimeout(this.warningId);
    this.timeoutId = null;
    this.warningId = null;
    
    // Save to localStorage
    try {
      localStorage.setItem('lastActiveTime', this.lastActiveTime.toString());
    } catch (e) {
      console.error('Error saving lastActiveTime:', e);
    }
  },
  
  cleanup() {
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.warningId) clearTimeout(this.warningId);
    
    // Remove all event listeners
    this.eventListeners.forEach(({ event, handler }) => {
      document.removeEventListener(event, handler, true);
    });
    
    this.eventListeners = [];
    this.isInitialized = false;
    
    console.log('🧹 Inactivity manager cleaned up');
  }
};

// Throttle utility - stable implementation
function createThrottle(delay: number) {
  let lastCall = 0;
  
  return function throttle(callback: Function) {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      callback();
    }
  };
}

export const useInactivityLogout = ({
  timeoutMinutes = 30,
  warningMinutes = 5,
  enabled = true
}: UseInactivityLogoutOptions = {}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;
  
  // Create stable throttle function
  const throttle = useRef(createThrottle(5000)).current;

  const logout = useCallback(async () => {
    const currentUser = pb.authStore.model;
    
    console.log('🚪 Logging out due to inactivity');
    
    try {
      if (currentUser) {
        console.log('🔴 Setting user offline during inactivity logout:', { 
          userId: currentUser.id, 
          role: currentUser.role 
        });
        await setUserOffline(currentUser.id, currentUser.role);
      }
    } catch (error) {
      console.error('💥 Error setting user offline during inactivity logout:', error);
    }
    
    // Clear localStorage
    try {
      localStorage.removeItem('lastActiveTime');
    } catch (e) {
      console.error('Error removing lastActiveTime:', e);
    }
    
    pb.authStore.clear();
    
    toast({
      title: "Session Expired",
      description: "You have been logged out due to inactivity",
      variant: "destructive"
    });
    
    navigate('/auth');
  }, [navigate, toast]);

  const showWarning = useCallback(() => {
    if (inactivityManager.warningShown) return;
    
    inactivityManager.warningShown = true;
    
    toast({
      title: "Session Warning",
      description: `You will be logged out in ${warningMinutes} minutes due to inactivity`,
      duration: 10000
    });
    
    console.log(`⚠️ Inactivity warning shown - ${warningMinutes} minutes until logout`);
  }, [warningMinutes, toast]);

  const resetTimer = useCallback(() => {
    if (!enabled || !pb.authStore.isValid) return;
    
    // Clear existing timers
    if (inactivityManager.timeoutId) clearTimeout(inactivityManager.timeoutId);
    if (inactivityManager.warningId) clearTimeout(inactivityManager.warningId);
    
    // Reset state
    inactivityManager.reset();
    
    // Set new timers
    inactivityManager.warningId = setTimeout(showWarning, warningMs);
    inactivityManager.timeoutId = setTimeout(logout, timeoutMs);
  }, [enabled, logout, showWarning, timeoutMs, warningMs]);

  const handleActivity = useCallback(() => {
    if (!enabled || !pb.authStore.isValid) return;
    
    // Only process if tab is visible (unless on mobile)
    if (document.hidden && !/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      return;
    }
    
    throttle(() => {
      resetTimer();
    });
  }, [enabled, resetTimer, throttle]);

  const handleVisibilityChange = useCallback(() => {
    if (!enabled || !pb.authStore.isValid) return;

    if (document.hidden) {
      console.log('👁️ Tab hidden at:', new Date().toISOString());
    } else {
      console.log('👁️ Tab visible at:', new Date().toISOString());
      
      const timeSinceLastActivity = Date.now() - inactivityManager.lastActiveTime;
      const minutesInactive = Math.floor(timeSinceLastActivity / 60000);
      
      console.log(`Inactive for ${minutesInactive} minutes (${timeSinceLastActivity}ms)`);
      
      // Force logout if exceeded timeout
      if (timeSinceLastActivity >= timeoutMs) {
        console.log('⚠️ FORCING LOGOUT - Exceeded timeout while tab was hidden');
        logout();
        return;
      }
      
      // Show warning if in warning period
      if (timeSinceLastActivity >= warningMs && !inactivityManager.warningShown) {
        showWarning();
      }
      
      // Reset timers with remaining time
      resetTimer();
    }
  }, [enabled, logout, showWarning, resetTimer, timeoutMs, warningMs]);

  useEffect(() => {
    console.log('🔧 useInactivityLogout initializing:', { 
      enabled, 
      isValid: pb.authStore.isValid,
      user: pb.authStore.model?.id,
      isAlreadyInitialized: inactivityManager.isInitialized
    });
    
    // If disabled, clear any stale localStorage and exit - NO logout checks
    if (!enabled) {
      console.log('❌ Inactivity logout disabled - clearing stale data');
      try {
        localStorage.removeItem('lastActiveTime');
      } catch (e) {
        // Ignore errors
      }
      inactivityManager.cleanup();
      return;
    }
    
    if (!pb.authStore.isValid) {
      console.log('❌ No valid auth - skipping inactivity tracking');
      return;
    }

    // SINGLETON: Only allow one instance to initialize
    if (inactivityManager.isInitialized) {
      console.log('✅ Inactivity manager already initialized - skipping');
      return;
    }
    
    inactivityManager.isInitialized = true;
    console.log('✅ Inactivity manager initializing for the first time');

    // Initialize lastActiveTime from localStorage or current time
    try {
      const stored = localStorage.getItem('lastActiveTime');
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed)) {
          inactivityManager.lastActiveTime = parsed;
        }
      }
    } catch (e) {
      console.error('Error reading lastActiveTime:', e);
    }

    // Check if session is too old on mount
    const timeSinceLastActivity = Date.now() - inactivityManager.lastActiveTime;
    if (timeSinceLastActivity >= timeoutMs) {
      console.log('⚠️ Session too old on startup - forcing logout');
      logout();
      return;
    }

    // Activity events to monitor
    const events = [
      'mousedown',
      'mousemove',
      'keypress', 
      'keydown',
      'touchstart',
      'touchmove',
      'touchend',
      'click',
      'scroll',
      'wheel'
    ];

    // Add event listeners
    events.forEach(event => {
      const handler = handleActivity as EventListener;
      document.addEventListener(event, handler, true);
      inactivityManager.eventListeners.push({ event, handler });
    });

    // Add visibility change listener
    const visibilityHandler = handleVisibilityChange as EventListener;
    document.addEventListener('visibilitychange', visibilityHandler);
    inactivityManager.eventListeners.push({ event: 'visibilitychange', handler: visibilityHandler });

    // Start the timer
    resetTimer();

    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up inactivity logout');
      inactivityManager.cleanup();
    };
  }, [enabled, handleActivity, handleVisibilityChange, resetTimer, logout, timeoutMs]);

  return {
    resetTimer: handleActivity,
    logout
  };
};